import * as React from "react";
import { useState } from "react";
import { CircularProgress, Grid2 as Grid, Tooltip } from "@mui/material";
import { OrderDataDTO, PrintType } from "@/api/data-contracts";
import { ChangeHandler } from "@/lib/modelstate";
import { ErrorsShape } from "@/lib/utils";
import { MILLIMETRE_UNIT, TOOLTIP_DELAY_MS } from "@/constants";
import { CardEx } from "@/lib/components/CardEx";
import AutocompleteInputField from "@/lib/components/inputfields/auto-complete-input-field";
import { printOptions, useCartonGrades, useFefcoCodes } from "@/datacaches";
import NumberInputField from "@/lib/components/inputfields/number-input-field";
import CheckboxInputField from "@/lib/components/inputfields/checkbox-input-field";
import { GroupBox } from "@/lib/components/GroupBox";
import SelectInputField from "@/lib/components/inputfields/select-input-field";
import { Constants } from "@/api/constants";
import { useTranslation } from "react-i18next";
import { CommonI18N, RequestI18N } from "@/translations";
import { OrderDataType } from "@/model/orderData";
import { useCardChangeColors } from "@/common/request-cards/common";
import { useDialogController } from "@/lib/dialog-controller";
import StringInputField from "@/lib/components/inputfields/string-input-field";
import SearchIcon from "@mui/icons-material/Search";
import IconButton from "@mui/material/IconButton";
import { GradeParametersTable } from "@/lib/components/GradeParametersTable";
import { GradeStatusIndicator } from "@/common/grade-indicator/GradeStatusIndicator";
import Box from "@mui/material/Box";
import { CartonGradeSelectorDialog } from "@/common/dialogs/CartonGradeDialog/CartonGradeSelectorDialog";
import { color_grey } from "@/agentur/AgenturColors";

export type ProductCardProps = {
    orderData?: OrderDataDTO;
    onChange: ChangeHandler;
    errors?: ErrorsShape<{ orderData?: OrderDataDTO }>;
    readonly?: boolean;
    mode: OrderDataType;
    orgData?: OrderDataDTO | null;
};

export const ProductCard = ({ orderData, orgData, errors, onChange, readonly, mode }: ProductCardProps) => {
    const { t } = useTranslation();
    const { getGridChangeSx, getCheckboxChangeSx } = useCardChangeColors();
    const [fefcoCodesOptions] = useFefcoCodes();
    const [grades] = useCartonGrades();
    const printSelectOptions = printOptions.map((option) => ({
        label: option && t(CommonI18N.enum.printType[option]),
        value: option
    }));
    const readonlyEx = readonly || mode === "quote";
    const [DialogNode, showDialog] = useDialogController();
    const [selectedLocations, setSelectedLocations] = useState<string[]>([]);

    // Hilfsfunktion zum Vergleich der CartonGrade-Parameter
    const hasCartonGradeChanged = React.useMemo(() => {
        if (!orgData?.cartonGrade || !orderData?.cartonGrade) {
            return false;
        }

        const org = orgData.cartonGrade;
        const current = orderData.cartonGrade;

        return (
            org.code !== current.code ||
            org.outerLiner !== current.outerLiner ||
            org.fluteOne !== current.fluteOne ||
            org.linerOne !== current.linerOne ||
            org.fluteTwo !== current.fluteTwo ||
            org.linerTwo !== current.linerTwo ||
            org.fluteThree !== current.fluteThree ||
            org.innerLiner !== current.innerLiner ||
            org.weight !== current.weight ||
            org.edgeCrushTest !== current.edgeCrushTest ||
            org.wetStrength !== current.wetStrength ||
            org.flute !== current.flute
        );
    }, [orgData?.cartonGrade, orderData?.cartonGrade]);

    const helperTextCartonGrade = hasCartonGradeChanged ? orgData?.cartonGrade?.code : undefined;

    const gradesLoading = React.useMemo(() => !grades || grades.length === 0, [grades]);

    const selectedGrade = React.useMemo(() => {
        if (!orderData?.cartonGrade?.code || !grades) {
            return undefined;
        }
        // Try to find the grade in the grades array
        const foundGrade = grades.find((g) => g.values?.code === orderData.cartonGrade.code);
        if (foundGrade) {
            return foundGrade;
        }

        // If not found, it might be a custom grade, so create a grade object with the values
        if (orderData?.cartonGrade) {
            return {
                values: orderData.cartonGrade,
                importedAt: undefined // null importedAt indicates a custom grade (will show FREE emoji)
            };
        }
        return undefined;
    }, [grades, orderData?.cartonGrade]);

    // Finden der Original-Sorte basierend auf dem Code aus orgData
    const originalGrade = React.useMemo(() => {
        if (!orgData?.cartonGrade?.code || !grades) {
            return undefined;
        }
        const foundGrade = grades.find((g) => g.values?.code === orgData.cartonGrade.code);
        if (foundGrade) {
            return foundGrade;
        }

        if (orgData?.cartonGrade) {
            return {
                values: orgData.cartonGrade,
                importedAt: undefined
            };
        }
        return undefined;
    }, [grades, orgData?.cartonGrade]);

    const openGradeSelector = () => {
        showDialog((open, close) => (
            <CartonGradeSelectorDialog
                open={open}
                close={close}
                grades={grades}
                initialFilter={selectedGrade?.importedAt ? selectedGrade.values?.code : ""}
                mode={mode}
                selectedLocations={selectedLocations}
                onLocationsChange={setSelectedLocations}
                originalGrade={originalGrade}
                selectedGrade={selectedGrade}
                onSelect={(grade) => {
                    onChange({ name: "orderData.cartonGrade", value: grade.values });
                    close();
                }}
            />
        ));
    };

    return (
        <CardEx title={t(RequestI18N.details.form.product)}>
            <Grid container spacing={3} direction="column">
                <Grid size={{ sm: 12 }} container direction="row" spacing={2} justifyContent="stretch">
                    <Grid size={{ sm: 6 }}>
                        <AutocompleteInputField
                            name="orderData.fefcoCode"
                            label={t(RequestI18N.details.field.fefcoCode)}
                            onChange={onChange}
                            value={orderData?.fefcoCode}
                            error={errors?.orderData?.fefcoCode}
                            options={fefcoCodesOptions}
                            maxChars={Constants.FEFCO_CODE_LENGTH_MAX}
                            disabled={readonlyEx}
                            freeSolo
                        />
                    </Grid>
                    <Grid size={{ sm: 6 }} sx={getGridChangeSx(orgData, hasCartonGradeChanged)}>
                        <Tooltip
                            title={
                                <GradeParametersTable grade={selectedGrade} originalGrade={originalGrade} mode={mode} />
                            }
                            enterDelay={TOOLTIP_DELAY_MS}
                            enterNextDelay={TOOLTIP_DELAY_MS}
                            placement="bottom-start"
                            slotProps={{
                                tooltip: {
                                    sx: {
                                        maxWidth: "fit-content",
                                        padding: "4px"
                                    }
                                }
                            }}
                            arrow
                            disableHoverListener={!selectedGrade}
                        >
                            <Box>
                                <StringInputField
                                    name="orderData.cartonGrade"
                                    label={t(RequestI18N.details.field.variety)}
                                    value={orderData?.cartonGrade.code ?? ""}
                                    disabled={readonly}
                                    error={errors?.orderData?.cartonGrade?.code}
                                    helperText={helperTextCartonGrade}
                                    endAdornment={
                                        <>
                                            {selectedGrade && (
                                                <GradeStatusIndicator importedAt={selectedGrade.importedAt} />
                                            )}
                                            <Tooltip
                                                title={
                                                    gradesLoading ? t(RequestI18N.details.message.varietyLoading) : ""
                                                }
                                            >
                                                <span>
                                                    <IconButton
                                                        onClick={openGradeSelector}
                                                        edge="end"
                                                        size="small"
                                                        disabled={readonly || gradesLoading}
                                                        tabIndex={-1}
                                                    >
                                                        {gradesLoading ? (
                                                            <CircularProgress
                                                                size="1em"
                                                                sx={{ color: color_grey[1] }}
                                                            />
                                                        ) : (
                                                            <SearchIcon />
                                                        )}
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                        </>
                                    }
                                />
                            </Box>
                        </Tooltip>
                        <DialogNode />
                    </Grid>
                </Grid>
                <Grid size={{ sm: 12 }} container direction="row" spacing={2}>
                    <Grid size={{ sm: 4 }}>
                        <NumberInputField
                            name="orderData.internalDimensionLength"
                            label={t(RequestI18N.details.field.length)}
                            onChange={onChange}
                            value={orderData?.internalDimensionLength}
                            error={errors?.orderData?.internalDimensionLength}
                            suffix={` ${MILLIMETRE_UNIT}`}
                            min={Constants.INTERNAL_DIMENSION_MIN}
                            max={Constants.INTERNAL_DIMENSION_MAX}
                            disabled={readonlyEx}
                        />
                    </Grid>
                    <Grid size={{ sm: 4 }}>
                        <NumberInputField
                            name="orderData.internalDimensionWidth"
                            label={t(RequestI18N.details.field.width)}
                            onChange={onChange}
                            value={orderData?.internalDimensionWidth}
                            error={errors?.orderData?.internalDimensionWidth}
                            suffix={` ${MILLIMETRE_UNIT}`}
                            min={Constants.INTERNAL_DIMENSION_MIN}
                            max={Constants.INTERNAL_DIMENSION_MAX}
                            disabled={readonlyEx}
                        />
                    </Grid>
                    <Grid size={{ sm: 4 }}>
                        <NumberInputField
                            name="orderData.internalDimensionHeight"
                            label={t(RequestI18N.details.field.height)}
                            onChange={onChange}
                            value={orderData?.internalDimensionHeight}
                            error={errors?.orderData?.internalDimensionHeight}
                            suffix={` ${MILLIMETRE_UNIT}`}
                            min={Constants.INTERNAL_DIMENSION_MIN}
                            max={Constants.INTERNAL_DIMENSION_MAX}
                            disabled={readonlyEx}
                        />
                    </Grid>
                </Grid>
                <Grid size={{ sm: 12 }} container direction="row" spacing={2}>
                    <Grid size={{ sm: 6 }}>
                        <GroupBox
                            title={t(RequestI18N.details.field.closureType)}
                            paddingTop={12}
                            paddingBottom={12}
                            disabled={readonlyEx}
                        >
                            <Grid container direction="row" spacing={1}>
                                <Grid>
                                    <CheckboxInputField
                                        name="orderData.glued"
                                        label={t(RequestI18N.details.field.glue)}
                                        onChange={onChange}
                                        value={orderData?.glued}
                                        error={errors?.orderData?.glued}
                                        dense
                                        disabled={readonly}
                                        sx={getCheckboxChangeSx(orgData, orgData?.glued !== orderData?.glued)}
                                    />
                                </Grid>
                                <Grid>
                                    <CheckboxInputField
                                        name="orderData.stitched"
                                        label={t(RequestI18N.details.field.staple)}
                                        onChange={onChange}
                                        value={orderData?.stitched}
                                        error={errors?.orderData?.stitched}
                                        dense
                                        disabled={readonly}
                                        sx={getCheckboxChangeSx(orgData, orgData?.stitched !== orderData?.stitched)}
                                    />
                                </Grid>
                                <Grid>
                                    <CheckboxInputField
                                        name="orderData.taped"
                                        label={t(RequestI18N.details.field.tap)}
                                        onChange={onChange}
                                        value={orderData?.taped}
                                        error={errors?.orderData?.taped}
                                        dense
                                        disabled={readonly}
                                        sx={getCheckboxChangeSx(orgData, orgData?.taped !== orderData?.taped)}
                                    />
                                </Grid>
                            </Grid>
                        </GroupBox>
                    </Grid>
                    <Grid size={{ sm: 6 }}>
                        <GroupBox
                            title={t(RequestI18N.details.page.closureHelpCardTitle)}
                            paddingTop={12}
                            paddingBottom={12}
                            disabled={readonlyEx}
                        >
                            <Grid container direction="row" spacing={0}>
                                <Grid>
                                    <CheckboxInputField
                                        name="orderData.siliconeTape"
                                        label={t(RequestI18N.details.field.siliconTape)}
                                        onChange={onChange}
                                        value={orderData?.siliconeTape}
                                        error={errors?.orderData?.siliconeTape}
                                        dense
                                        disabled={readonlyEx}
                                    />
                                </Grid>
                                <Grid>
                                    <CheckboxInputField
                                        name="orderData.tearTape"
                                        label={t(RequestI18N.details.field.tearTape)}
                                        onChange={onChange}
                                        value={orderData?.tearTape}
                                        error={errors?.orderData?.tearTape}
                                        dense
                                        disabled={readonlyEx}
                                    />
                                </Grid>
                            </Grid>
                        </GroupBox>
                    </Grid>
                </Grid>
                <Grid size={{ sm: 12 }} container direction="row" spacing={2}>
                    <Grid size={{ sm: 6 }} container spacing={2}>
                        <Grid size={{ sm: 6 }}>
                            <SelectInputField
                                name="orderData.externalPrint"
                                label={t(RequestI18N.details.field.externalPrint)}
                                value={orderData?.externalPrint}
                                error={errors?.orderData?.externalPrint}
                                options={printSelectOptions}
                                displayEmpty={true}
                                onChange={onChange}
                                disabled={readonlyEx}
                            />
                        </Grid>
                        <Grid size={{ sm: 6 }}>
                            <NumberInputField
                                name="orderData.externalNumberOfColors"
                                label={t(RequestI18N.details.field.numberOfColors)}
                                onChange={onChange}
                                value={orderData?.externalNumberOfColors}
                                error={errors?.orderData?.externalNumberOfColors}
                                disabled={
                                    readonlyEx ||
                                    orderData?.externalPrint == null ||
                                    orderData?.externalPrint === PrintType.NO_PRINT
                                }
                                min={Constants.NUMBER_OF_COLORS_MIN}
                                max={Constants.NUMBER_OF_COLORS_MAX}
                            />
                        </Grid>
                    </Grid>
                    <Grid size={{ sm: 6 }} container spacing={2}>
                        <Grid size={{ sm: 6 }}>
                            <SelectInputField
                                name="orderData.internalPrint"
                                label={t(RequestI18N.details.field.internalPrint)}
                                value={orderData?.internalPrint}
                                error={errors?.orderData?.internalPrint}
                                options={printSelectOptions}
                                displayEmpty
                                onChange={onChange}
                                disabled={readonlyEx}
                            />
                        </Grid>
                        <Grid size={{ sm: 6 }}>
                            <NumberInputField
                                name="orderData.internalNumberOfColors"
                                label={t(RequestI18N.details.field.numberOfColors)}
                                value={orderData?.internalNumberOfColors}
                                onChange={onChange}
                                error={errors?.orderData?.internalNumberOfColors}
                                disabled={
                                    readonlyEx ||
                                    orderData?.internalPrint == null ||
                                    orderData?.internalPrint === PrintType.NO_PRINT
                                }
                                min={Constants.NUMBER_OF_COLORS_MIN}
                                max={Constants.NUMBER_OF_COLORS_MAX}
                            />
                        </Grid>
                    </Grid>
                </Grid>
                <Grid size={{ sm: 12 }} container direction="row" spacing={1}>
                    <Grid container size={{ sm: 6 }}>
                        <Grid>
                            <CheckboxInputField
                                name="orderData.dangerousGoods"
                                label={t(RequestI18N.details.field.dangerousGoods)}
                                onChange={onChange}
                                value={orderData?.dangerousGoods}
                                error={errors?.orderData?.dangerousGoods}
                                dense
                                disabled={readonlyEx}
                            />
                        </Grid>
                    </Grid>
                    <Grid container size={{ sm: 6 }}>
                        <Grid>
                            <CheckboxInputField
                                name="orderData.contractGluing"
                                label={t(RequestI18N.details.field.contractGluing)}
                                onChange={onChange}
                                value={orderData?.contractGluing}
                                error={errors?.orderData?.contractGluing}
                                dense
                                disabled={readonlyEx}
                            />
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        </CardEx>
    );
};
